import { Injectable } from '@angular/core';
import { EnumList } from '../interfaces/i-enum';
import { ActivatedRouteSnapshot } from '@angular/router';
import { OverlayService } from '@clavisco/overlay';
import { finalize, tap, catchError, Observable } from 'rxjs';
import { IPermission } from '../interfaces/i-permission';
import { ReportService } from './report.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalService {

  constructor(
    private clBlockUI: OverlayService,
    private reportService: ReportService
  ) { }


  /**
 * Converts an enum into a list of objects with translated names.
 * 
 * @param enumType - The enum to convert.
 * @param translations - An optional mapping of enum keys to translated names.
 * @returns A list of objects containing the enum values and their names.
 */
  ConvertEnumIntoList<T extends Record<string, string | number>>(
    enumType: T,
    translations?: Record<string, string>
  ): EnumList[] {
    let map: EnumList[] = [];

    for (const key in enumType) {
      if (!isNaN(Number(key))) continue; // Ignore numeric keys (reverse mapping of enums)

      map.push({
        id: enumType[key] as number,
        name: translations?.[key] || key // Use translation if available, otherwise use enum key
      });
    }

    return map;
  }
  /**
   * Gets the required permission for a given route.
   * 
   * @param route - The route for which the permission is required.
   * @returns The name of the permission required for that route.
   */
  GetPermissionsByPath(route: string): string {
    let reqPerm: string = '';
    switch (route) {
      case 'settings':
        reqPerm = 'M_Settings';
        break;
      case 'logs':
        reqPerm = 'M_Logs';
        break;
      case 'documents':
        reqPerm = 'M_Documents';
        break;
      case 'invoice':
        reqPerm = 'S_Invoice';
        break;
      case 'logsDocuments':
        reqPerm = 'S_LogsDocuments';
        break;
      case 'logsViewer':
        reqPerm = 'S_PhysicalLogs';
        break;
      case 'users':
        reqPerm = 'S_Users';
        break;
      case 'rolePerm':
        reqPerm = 'S_RolsPerm';
        break;
      default:
        break;
    }
    return reqPerm;
  }

  /**
 * Validates if a specific permission is present in a list of permissions.
 * 
 * @param perms - List of permissions to validate against.
 * @param reqPerm - The required permission to validate.
 * @returns True if the required permission is present in the list, otherwise false.
 */
  ValidatePermission(perms: IPermission[] | null, reqPerm: string): boolean {
    if (!perms) return false;
    return perms.some((p: IPermission) => p.Name === reqPerm);
  }

  /**
   * Gets the full path of an active route, including child routes.
   * 
   * @param route - The ActivatedRouteSnapshot object containing the current route.
   * @returns The full route path as a string.
   */
  getRoutePath(route: ActivatedRouteSnapshot): string {
    let routePath = '';
    if (route.routeConfig) {
      routePath += route.routeConfig.path;
    }
    if (route.firstChild) {
      routePath += this.getRoutePath(route.firstChild);
    }
    return routePath;
  }

  /**
   * Formats a number as currency in the format "$X.XX".
   * 
   * @param amount - The amount to format.
   * @returns The formatted currency string.
   */
  formatAmount(amount: number): string {
    const formatted = new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return `$${formatted}`;
  }

  formatDate(date: Date): string {
    const newDate: Date = new Date(date);
    const year = newDate.getFullYear();
    const month = (newDate.getMonth() + 1).toString().padStart(2, '0');
    const day = (newDate.getDate()).toString().padStart(2, '0');
    const hour = (newDate.getHours()).toString().padStart(2, '0');
    const minute = (newDate.getMinutes()).toString().padStart(2, '0');
    const second = (newDate.getSeconds()).toString().padStart(2, '0');

    return `${month}/${day}/${year} ${hour}:${minute}:${second}`;
  }

  /**
   * Previews a report by fetching the draft document and opening it in a new tab.
   * 
   * @param studentCardCode - The studen Cardcode for which to fetch the report.
   * @param month - The studen month for which to fetch the report.
   * @param year - The studen year for which to fetch the report.
   */
  PreviewDraftReport(studentCardCode: string, month: string, year: string): void {
    this.previewReport(
      "Previsualizando reporte del preliminar",
      () => this.reportService.GetDraftReport(studentCardCode, month, year)
    );
  }

  /**
   * Previews a report by fetching the invoice document and opening it in a new tab.
   * 
   * @param studentCardCode - The studen Cardcode for which to fetch the report.
   * @param month - The studen month for which to fetch the report.
   * @param year - The studen year for which to fetch the report.
   */
  PreviewInvoiceReport(studentCardCode: string, month: string, year: string): void {
    this.previewReport(
      "Previsualizando reporte de la factura",
      () => this.reportService.GetInvoiceReport(studentCardCode, month, year)
    );
  }

  /**
   * Handles the common logic for previewing a report by fetching the document and opening it in a new tab.
   * 
   * @param message - The message to display while fetching the report.
   * @param fetchReport - A function that returns the observable for fetching the report.
   */
  private previewReport<T>(message: string, fetchReport: () => Observable<{ Data: string }>): void {
    this.clBlockUI.OnGet(message);

    fetchReport()
      .pipe(
        finalize(() => this.clBlockUI.Drop()), // Asegura que el bloqueo de UI se elimine al finalizar
        catchError((error) => {
          console.error("Error al obtener el reporte:", error); // Log de errores para depuración
          return []; // Retorna un observable vacío para evitar interrupciones
        })
      )
      .subscribe((data) => {
        if (data?.Data) {
          this.openPdfInNewTab(data.Data); // Abre el PDF solo si los datos son válidos
        } else {
          console.error("No se recibieron datos válidos para el reporte.");
        }
      });
  }

  /**
   * Decodes a base64 string and opens the resulting PDF in a new browser tab.
   * 
   * @param base64Data - The base64-encoded string representing the PDF file.
   */
  private openPdfInNewTab(base64Data: string): void {
    const fileBase64 = atob(base64Data);
    const length = fileBase64.length;
    const arrayBuffer = new ArrayBuffer(length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < length; i++) {
      uintArray[i] = fileBase64.charCodeAt(i);
    }

    const pdfFile = new Blob([uintArray], { type: 'application/pdf' });
    const fileUrl = URL.createObjectURL(pdfFile);
    const tab = window.open();

    if (tab !== null) {
      tab.location.href = fileUrl;
    }
  }
}
